import { format } from 'date-fns';
import { useMemo } from 'react';

import { type Board } from '@air/api/types';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';
import { formatFullName, parseAirDateToISO } from '@air/utilities';

import { useUserByAccountIdOrEmail } from '~/swr-hooks/members/useUserByAccountIdOrEmail';

export const usePrivateBoardMetadataMenuOptions = (
  board?: Pick<Board, 'creatorId' | 'createdAt'>,
): DropdownMenuOption[] => {
  const { user } = useUserByAccountIdOrEmail({ userId: board?.creatorId ?? '' });

  return useMemo<DropdownMenuOption[]>(() => {
    if (!board) {
      return [];
    }

    const creatorName = user ? formatFullName(user.firstName, user.lastName) || user.email : null;
    const formattedDate = format(parseAirDateToISO(board.createdAt), 'MMMM d, yyyy');

    const metadataRows = [
      ...(creatorName ? [{ label: 'Creator', value: creatorName }] : []),
      { label: 'Date created', value: formattedDate },
    ];

    return [
      { id: 'metadata-separator', type: 'separator' },
      {
        id: 'metadata',
        type: 'children',
        children: (
          <div className="flex flex-col gap-2 px-3 py-1">
            {metadataRows.map(({ label, value }) => (
              <div key={label} className="flex items-start justify-between gap-4 text-12">
                <span className="ads:text-metadata text-gray-10">{label}</span>
                <span className="max-w-[140px] text-right font-medium ads:text-primary text-gray-12">{value}</span>
              </div>
            ))}
          </div>
        ),
      },
    ];
  }, [board, user]);
};
